import type { UseQueryResult } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { QueryBoundary } from "../QueryBoundary";
import { Skel } from "../Skel";

// The «nómina» (provider radiogroup) while GET /providers loads, shared
// by the three places that pick a contratista: AssignBatchDrawer,
// GestionPanel's assign form and ProviderAssignForm. Same `.prov-list` /
// `.prov-option` boxes as the real radios (border, padding, gap), with
// the name and the meta line as text bars in their real line boxes, so
// the list lands without a shift.
//
// It is a section inside a drawer or a panel: no announcement of its own
// (the route owns the one role=status) and a load error that never moves
// focus out of the form the user may be filling.
type SkelProps = {
  /** The list's own classes: GestionPanel and ProviderAssignForm add
   *  `g-list`. */
  className?: string;
  /** The class of the meta line under the name (`field__hint`, or
   *  `cell-meta` in AssignBatchDrawer). */
  metaClassName?: string;
  rows?: number;
};

const NAME_WIDTHS = ["48%", "36%", "56%"] as const;

export function ProviderListSkel({
  className = "prov-list",
  metaClassName = "field__hint",
  rows = 3,
}: SkelProps) {
  return (
    <div className={className} aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="prov-option">
          <div className="prov-option__meta">
            <div className="prov-option__name">
              <Skel variant="text" width={NAME_WIDTHS[i % NAME_WIDTHS.length]} />
            </div>
            <div className={metaClassName}>
              <Skel variant="text" width="30%" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

type BoundaryProps = SkelProps & {
  query: UseQueryResult<unknown, unknown>;
  /** The real list (and its empty-nómina copy): rendered once the
   *  providers are in. It reads the data from the caller's own hooks. */
  children: ReactNode;
};

export function ProviderListBoundary({ query, children, ...skel }: BoundaryProps) {
  return (
    <QueryBoundary
      query={query}
      label="la nómina"
      kind="section"
      skeleton={<ProviderListSkel {...skel} />}
    >
      {() => children}
    </QueryBoundary>
  );
}
